/* eslint-disable spire/fenced-imports */
import Zone from "@insite/client-framework/Components/Zone";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import loadAddressFields from "@insite/client-framework/Store/Data/AddressFields/Handlers/LoadAddressFields";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getCountriesDataView } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { getWORData, setInLocalStorage } from "@tKgpco/Services/WORService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import validateOneTimeAddressHandler from "@tKgpco/Store/Data/ShipToAddressValidation/Handlers/validateShipToAddress";
import React, { Component, createContext, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    loadCurrentBillTo,
    loadCurrentShipTo,
    loadAddressFields,
    loadCurrentCountries,
    validateOneTimeAddressHandler,
};

const mapStateToProps = (state: CustomApplicationState) => {
    const addressFieldsDataView = getAddressFieldsDataView(state);
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        currentBillToState: getCurrentBillToState(state),
        currentShipToState: getCurrentShipToState(state),
        shouldLoadAddressFields: !addressFieldsDataView.value && !addressFieldsDataView.isLoading,
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        USaddressErrorMessage: state?.KGPState.USPS.USvalidateErrorMessage,
        isUSAddressValidionLoading: state?.KGPState.USPS.isLoading
    };
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
    shippingAddress: ShipToModel;
    billingAddress: BillToModel;
    useOneTimeAddress: boolean;
    useOrderShipto: boolean;
    formState: ShipToModel;
    formErrors: Record<string, string>;
    worDeliveryInfo: any;
};

export const WORCheckoutShippingFormContext = createContext<State & { dispatch: (updatedState: any) => void }>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
    shippingAddress: {} as ShipToModel,
    billingAddress: {} as BillToModel,
    dispatch: () => {},
    formState: {} as ShipToModel,
    formErrors: {},
    useOneTimeAddress: false,
    useOrderShipto: true,
    worDeliveryInfo: {},
});

export const useWORCheckoutShippingFormContext: () => any = () => {
    const ctx = useContext(WORCheckoutShippingFormContext);

    if (!ctx) {
        throw new Error("Accessing WORContext outside scope!");
    }
    return ctx;
};

class WORCheckoutShippingPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
            shippingAddress: {} as ShipToModel,
            billingAddress: {} as BillToModel,
            useOneTimeAddress: false,
            useOrderShipto: true,
            formState: {} as ShipToModel,
            formErrors: {
                companyName: "",
                address1: "",
                country: "",
                postalCode: "",
                phone: "",
                firstName: "",
                lastName: "",
                city: "",
            },
            worDeliveryInfo: {},
        };
    }

    async componentDidMount() {
        // TODO: Load Addresses here
    }

    UNSAFE_componentWillMount() {
        const {
            currentBillToState,
            currentShipToState,
            shouldLoadAddressFields,
            shouldLoadCountries,
            loadCurrentBillTo,
            loadCurrentShipTo,
            loadAddressFields,
            loadCurrentCountries,
        } = this.props;

        if (!currentBillToState.value && !currentBillToState.isLoading) {
            loadCurrentBillTo();
        }
        if (!currentShipToState.value && !currentShipToState.isLoading) {
            loadCurrentShipTo();
        }
        if (shouldLoadAddressFields) {
            loadAddressFields();
        }
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }

    scrollToFirstFormError = () => {
        const firstErrorElement = document.querySelector("[aria-invalid='true']");
        if (firstErrorElement) {
            const formFieldParent = firstErrorElement.closest("[class*='FormFieldStyle']");
            if (!formFieldParent) {
                return;
            }
            const { top } = formFieldParent.getBoundingClientRect();
            window.scrollTo({
                top: top + window.pageYOffset,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (this.state.useOneTimeAddress) {
            this.props.validateOneTimeAddressHandler({
                address1: this.state.formState.address1,
                address2: this.state.formState.address2,
                city: this.state.formState.city,
                stateId: this.state.formState.state!.id,
                countryId: this.state.formState.country!.id,
                oneTimeAddress: true,
                postalCode: this.state.formState.postalCode,
                onComplete: () => {
                    setTimeout(() => {
                        if (!this.props.USaddressErrorMessage) {
                            this.submitForm();
                        }
                    }, 1000);
                },
            });
        } else {
            this.submitForm();
        }
    };

    handleSetState = (updatedState: any) => {
        this.setState({ ...updatedState });
    };

    submitForm = async () => {
        const worData = getWORData();

        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];

        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });

        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o)) {
            this.setState({ isRedirecting: true });
            setInLocalStorage({
                ...worData,
                billingAddress: this.state.billingAddress,
                shippingAddress: this.state.shippingAddress,
                useOneTimeAddress: this.state.useOneTimeAddress,
                useOrderShipto: this.state.useOrderShipto,
                formState: this.state.formState,
                formErrors: this.state.formErrors,
            });
            this.props.history.push("/WOR-CheckoutReviewSubmit");
        } else {
            this.scrollToFirstFormError();
        }
    };

    render() {
        return (
            <Page data-test-selector="checkoutShipping">
                <LoadingOverlay loading={this.props.isUSAddressValidionLoading}>
                    <WORCheckoutShippingFormContext.Provider value={{ ...this.state, dispatch: this.handleSetState }}>
                        <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                            <Zone zoneName="Content" contentId={this.props.id} />
                        </form>
                    </WORCheckoutShippingFormContext.Provider>
                </LoadingOverlay>
            </Page>
        );
    }
}
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(WORCheckoutShippingPage)),
    definition: {
        displayName: "WOR Checkout Shipping Page",
        group: "Checkout - Shipping",
    },
};

export default widgetModule;
